import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import { LineChart, AnnotatableChart } from '../../../../../shared/components/charts';
import { DashboardCard } from '../../../../../shared/components/DashboardCard';
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
  ComparisonToggle,
  GranularityToggle,
  ComparisonColumns,
  WeatherAnomalyCallout,
} from '../../../../../shared/components';
import { useSessionQuery } from '../../../../../features/auth/hooks';
import { useBiometricsTrends, useBiometricsPatterns } from '../hooks';
import { BiometricsStatsColumn } from '../components/BiometricsStatsColumn';
import { buildFilterParams, transformTrendsData, useResolvedGranularity } from '../../shared/utils';
import { useClientScope } from '../../../../../shared/contexts';
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
  useComparisonMode,
  useComparisonGroups,
  useComparisonTrends,
  useComparisonPatterns,
} from '../../../../../shared/hooks';
import { AiSummaryPanel, useAiSummary } from '../../../insights';
import { buildComparisonTrendSeries, buildComparisonPatternSeries } from '../../../../../shared/utils';

const BIOMETRICS_AND_VITALS_DETAILS = {
  biometricTrends: `Displays daily variations across core vital metrics, such as heart rate, heart rate variability (HRV), sleep, strain, and recovery. These metrics reflect changes in physiological load, autonomic balance, and overall readiness.

By tracking trends over time, this view helps identify emerging patterns, sustained shifts in stress or recovery status, and potential early indicators of fatigue or improved adaptation. It provides a longitudinal perspective on workforce health, supporting proactive wellbeing and performance management.`,
  averagePaceOfAging: `Represents the average rate of biological aging across the workforce, derived from longitudinal trends in key physiological markers including SpO2, respiration rate, resting heart rate, heart rate variability (HRV), and skin temperature.

These metrics reflect cardiopulmonary function, autonomic regulation, and systemic stress. Sustained deviations from optimal ranges may indicate accelerated physiological wear, while stable, favourable patterns suggest healthier biological aging trajectories over time.`,
  respiratoryRatePattern: `Displays the average hourly variation in breathing rate across a 24-hour period, illustrating how respiration fluctuates throughout the day and night. Respiratory rate reflects metabolic demand, physical activity levels, and autonomic nervous system regulation.

By examining these hourly patterns, this view helps identify typical daytime elevations linked to movement and exertion, as well as nocturnal reductions associated with rest and recovery. Consistent deviations from expected patterns may indicate increased physiological stress, fatigue, or changes in overall health status.`,
  skinTemperaturePattern: `Displays the average hourly changes in skin temperature over a 24-hour period, reflecting circadian rhythm dynamics and peripheral thermoregulation.

Daily fluctuations can indicate shifts in metabolic activity, sleep-wake cycles, and physiological stress. Monitoring these patterns helps identify deviations from typical temperature rhythms that may signal changes in recovery status or overall health.`,
};

export const BiometricsAndVitals: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { granularityMode, setGranularityMode, granularity } = useResolvedGranularity(dateRange);
  const [biometricTrendView, setBiometricTrendView] = useState('avg_heart_rate');

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId, null, null, { isCentralAdmin }),
    [filterState, dateRange, effectiveClientId, isCentralAdmin],
  );

  // Client events: central admins and client admins may annotate; the popover
  // is read-only for everyone else. Only enabled for a single selected client.
  const { data: session } = useSessionQuery();
  const canEditEvents = Boolean(isCentralAdmin || session?.is_admin);
  const [startDate, endDate] = dateRange;
  const eventStartDate = startDate ? startDate.format('YYYY-MM-DD') : undefined;
  const eventEndDate = endDate ? endDate.format('YYYY-MM-DD') : undefined;

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      avg_heart_rate: {
        min: typeof metric_min.avg_heart_rate === 'number' ? metric_min.avg_heart_rate : undefined,
        max: typeof metric_max.avg_heart_rate === 'number' ? metric_max.avg_heart_rate : undefined,
      },
      max_heart_rate: {
        min: typeof metric_min.max_heart_rate === 'number' ? metric_min.max_heart_rate : undefined,
        max: typeof metric_max.max_heart_rate === 'number' ? metric_max.max_heart_rate : undefined,
      },
      hrv: {
        min: typeof metric_min.hrv === 'number' ? metric_min.hrv : undefined,
        max: typeof metric_max.hrv === 'number' ? metric_max.hrv : undefined,
      },
      resting_heart_rate: {
        min: typeof metric_min.resting_heart_rate === 'number' ? metric_min.resting_heart_rate : undefined,
        max: typeof metric_max.resting_heart_rate === 'number' ? metric_max.resting_heart_rate : undefined,
      },
      respiratory_rate: {
        min: typeof metric_min.respiratory_rate === 'number' ? metric_min.respiratory_rate : undefined,
        max: typeof metric_max.respiratory_rate === 'number' ? metric_max.respiratory_rate : undefined,
      },
      spo2: {
        min: typeof metric_min.spo2 === 'number' ? metric_min.spo2 : undefined,
        max: typeof metric_max.spo2 === 'number' ? metric_max.spo2 : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  // Comparison mode: render one column/line per selected filter value.
  const comparison = useComparisonMode(filterState);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filterState,
    dateRange,
    effectiveClientId,
    comparison,
    isCentralAdmin,
  );

  const { buildPayload: buildAiPayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Biometrics & Vitals',
    groups,
    isComparing,
    dimension: comparison.dimension,
    filterParams,
    metricSpecs: [
      { label: 'Resting heart rate', keys: ['resting_heart_rate'] },
      { label: 'HRV', keys: ['hrv'] },
      { label: 'Respiratory rate', keys: ['respiratory_rate'] },
      { label: 'Blood oxygen (SpO2)', keys: ['spo2'] },
    ],
  });

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  // Trends
  const trendsQuery = useBiometricsTrends({
    ...filterParams,
    metrics: ['avg_heart_rate', 'max_heart_rate', 'respiratory_rate', 'spo2', 'hrv', 'resting_heart_rate'],
    granularity,
  });

  // Patterns
  const respiratoryPatternsQuery = useBiometricsPatterns({
    ...filterParams,
    metric: 'respiratory_rate_patterns',
    granularity: 'hourly',
  });

  const tempPatternsQuery = useBiometricsPatterns({
    ...filterParams,
    metric: 'skin_temperature_patterns',
    granularity: 'hourly',
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    const metrics = ['avg_heart_rate', 'max_heart_rate', 'respiratory_rate', 'spo2', 'hrv', 'resting_heart_rate'];
    const transformed = transformTrendsData(trendsQuery.data, metrics);
    const lastWithData = [...transformed]
      .reverse()
      .findIndex((point) => metrics.some((metric) => point[metric] !== null));
    if (lastWithData === -1) return [];
    const lastIndex = transformed.length - 1 - lastWithData;
    return transformed.slice(0, lastIndex + 1);
  }, [trendsQuery.data]);

  // ── Comparison overlays ──────────────────────────────────────────────────
  const comparisonTrends = useComparisonTrends(
    groups,
    ['avg_heart_rate', 'max_heart_rate', 'respiratory_rate', 'spo2', 'hrv', 'resting_heart_rate'],
    granularity,
  );
  const comparisonRespPatterns = useComparisonPatterns(groups, 'respiratory_rate_patterns', 'hourly');
  const comparisonTempPatterns = useComparisonPatterns(groups, 'skin_temperature_patterns', 'hourly');

  const trendCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonTrends.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, [biometricTrendView]),
        })),
        biometricTrendView,
        'label',
      ),
    [comparisonTrends, biometricTrendView],
  );
  const trendCompareLoading = comparisonTrends.some((e) => e.isLoading);

  const respPatternCompareChart = useMemo(
    () =>
      buildComparisonPatternSeries(
        comparisonRespPatterns.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: e.data?.data?.patterns ?? [],
        })),
        'time',
      ),
    [comparisonRespPatterns],
  );
  const respPatternCompareLoading = comparisonRespPatterns.some((e) => e.isLoading);

  const tempPatternCompareChart = useMemo(
    () =>
      buildComparisonPatternSeries(
        comparisonTempPatterns.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: e.data?.data?.patterns ?? [],
        })),
        'time',
      ),
    [comparisonTempPatterns],
  );
  const tempPatternCompareLoading = comparisonTempPatterns.some((e) => e.isLoading);

  const respiratoryRatePatternData = useMemo(() => {
    if (!respiratoryPatternsQuery.data?.data?.patterns) return [];
    return respiratoryPatternsQuery.data.data.patterns
      .filter((p) => p.time)
      .map((p) => ({
        time: p.time!,
        value: p.value,
        value_min: p.value_min ?? null,
        value_max: p.value_max ?? null,
      }));
  }, [respiratoryPatternsQuery.data]);

  const skinTemperaturePatternData = useMemo(() => {
    if (!tempPatternsQuery.data?.data?.patterns) return [];
    const patterns = tempPatternsQuery.data.data.patterns
      .filter((p) => p.time)
      .map((p) => ({
        time: p.time!,
        value: p.value,
        value_min: p.value_min ?? null,
        value_max: p.value_max ?? null,
      }));
    const values = patterns.map((item) => item.value).filter((value): value is number => typeof value === 'number');
    if (!values.length) return patterns;
    const average = Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 10) / 10;
    return patterns.map((item) => ({
      ...item,
      value: item.value ?? average,
    }));
  }, [tempPatternsQuery.data]);

  const skinTemperatureAxis = useMemo(() => {
    const values = skinTemperaturePatternData
      .map((item) => item.value)
      .filter((value): value is number => typeof value === 'number');
    if (!values.length) {
      return { min: 30, max: 38 };
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max(0.5, (max - min) * 0.1);
    return {
      min: Math.floor((min - padding) * 10) / 10,
      max: Math.ceil((max + padding) * 10) / 10,
    };
  }, [skinTemperaturePatternData]);

  const getTrendLabel = () => {
    switch (biometricTrendView) {
      case 'avg_heart_rate':
        return 'Avg HR';
      case 'max_heart_rate':
        return 'Max HR';
      case 'hrv':
        return 'Heart Rate Variability';
      case 'resting_heart_rate':
        return 'Resting HR';
      case 'respiratory_rate':
        return 'Respiratory Rate';
      case 'spo2':
        return 'Blood Oxygen (SpO2)';
      default:
        return 'Metric';
    }
  };

  const getTrendColor = () => {
    switch (biometricTrendView) {
      case 'avg_heart_rate':
        return theme.palette.info.main;
      case 'max_heart_rate':
        return theme.palette.error.main;
      case 'hrv':
        return theme.palette.success.main;
      case 'resting_heart_rate':
        return theme.palette.warning.main;
      case 'respiratory_rate':
        return theme.palette.warning.main;
      case 'spo2':
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getYAxisMax = () => {
    switch (biometricTrendView) {
      case 'avg_heart_rate':
      case 'resting_heart_rate':
        return undefined; // Auto scale
      case 'max_heart_rate':
        return undefined; // Auto scale
      case 'hrv':
        return undefined; // Auto scale
      case 'respiratory_rate':
        return 25;
      case 'spo2':
        return 100;
      default:
        return undefined;
    }
  };

  const isLoading = trendsQuery.isLoading || respiratoryPatternsQuery.isLoading || tempPatternsQuery.isLoading;

  const hasError = trendsQuery.isError || respiratoryPatternsQuery.isError || tempPatternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ComparisonToggle comparison={comparison} />
            <GranularityToggle value={granularityMode} onChange={setGranularityMode} />
            <DurationSelector value={dateRange} onChange={handleDateRangeChange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && <Alert severity='error'>Failed to load biometrics data. Please try again later.</Alert>}

        {/* Stat cards + Pace of Aging, one column per comparison group. */}
        <ComparisonColumns groups={groups} overflowCount={overflowCount}>
          {(group) => (
            <BiometricsStatsColumn
              filterParams={group.filterParams}
              isCentralAdmin={isCentralAdmin}
              isAnonymised={isAnonymised}
              stacked={isComparing}
            />
          )}
        </ComparisonColumns>

        <DashboardCard title='AI Summary' tooltip='AI narrative of the metrics shown.' expandable={false}>
          <AiSummaryPanel
            title={isComparing ? 'Compare these groups with AI' : 'Summarise this view with AI'}
            buildPayload={buildAiPayload}
            disabled={aiDisabled}
          />
        </DashboardCard>

        <WeatherAnomalyCallout filters={filterParams} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Biometric Trends'
              subtitle='Daily variations across core vital metrics.'
              metricKey={biometricTrendView}
              details={BIOMETRICS_AND_VITALS_DETAILS.biometricTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={biometricTrendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setBiometricTrendView(newValue);
                  }}
                  size='small'
                >
                  <ToggleButton value='avg_heart_rate'>Heart Rate</ToggleButton>
                  <ToggleButton value='hrv'>Heart Rate Variability</ToggleButton>
                  <ToggleButton value='resting_heart_rate'>Resting HR</ToggleButton>
                  <ToggleButton value='respiratory_rate'>Respiratory Rate</ToggleButton>
                  <ToggleButton value='spo2'>Blood Oxygen (SpO2)</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                {(isComparing ? trendCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  trendCompareChart.data.length > 0 ? (
                    <LineChart
                      data={trendCompareChart.data}
                      xAxisKey='label'
                      series={trendCompareChart.series}
                      height={300}
                      yAxisMin={0}
                      yAxisMax={getYAxisMax()}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No trend data available</Alert>
                    </Box>
                  )
                ) : trendsData.length > 0 ? (
                  <AnnotatableChart
                    clientId={effectiveClientId}
                    startDate={eventStartDate}
                    endDate={eventEndDate}
                    weatherFilters={filterParams}
                    canEdit={canEditEvents}
                    data={trendsData}
                    xAxisKey='label'
                    series={[
                      {
                        dataKey: biometricTrendView,
                        label: getTrendLabel(),
                        color: getTrendColor(),
                        area: true,
                      },
                      ...(biometricTrendView === 'avg_heart_rate'
                        ? [
                            {
                              dataKey: 'max_heart_rate',
                              label: 'Max HR',
                              color: theme.palette.error.main,
                              dashed: true,
                            },
                            {
                              dataKey: 'resting_heart_rate',
                              label: 'Resting HR',
                              color: theme.palette.success.main,
                              dashed: true,
                            },
                          ]
                        : []),
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[biometricTrendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[biometricTrendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>No trend data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        {/* Average Pace of Aging now renders inside each comparison column
            (BiometricsStatsColumn). Respiratory pattern overlays one line per
            group across the day in comparison mode. */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Respiratory Rate Pattern (24-Hour Average)'
              subtitle='Hourly variation in breathing rate throughout the day.'
              metricKey='respiratory_rate'
              details={BIOMETRICS_AND_VITALS_DETAILS.respiratoryRatePattern}
            >
              <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                {(isComparing ? respPatternCompareLoading : isLoading) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 230 }}>
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  respPatternCompareChart.data.length > 0 ? (
                    <LineChart
                      data={respPatternCompareChart.data}
                      xAxisKey='time'
                      series={respPatternCompareChart.series}
                      height={230}
                      yAxisMin={0}
                      yAxisMax={25}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 230 }}>
                      <Alert severity='info'>No pattern data available</Alert>
                    </Box>
                  )
                ) : respiratoryRatePatternData.length > 0 ? (
                  <LineChart
                    data={respiratoryRatePatternData}
                    xAxisKey='time'
                    series={[
                      {
                        dataKey: 'value',
                        label: 'Respiratory Rate',
                        color: theme.palette.warning.main,
                        area: true,
                      },
                    ]}
                    height={230}
                    yAxisMin={0}
                    yAxisMax={25}
                    showMinMaxLines
                  />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 230 }}>
                    <Alert severity='info'>No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title='Skin Temperature Pattern (24-Hour Average)'
              subtitle='Tracks changes in skin temperature across a full day.'
              metricKey='temperature'
              details={BIOMETRICS_AND_VITALS_DETAILS.skinTemperaturePattern}
            >
              <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                {(isComparing ? tempPatternCompareLoading : isLoading) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : isComparing ? (
                  tempPatternCompareChart.data.length > 0 ? (
                    <LineChart
                      data={tempPatternCompareChart.data}
                      xAxisKey='time'
                      series={tempPatternCompareChart.series}
                      height={300}
                      yAxisMin={skinTemperatureAxis.min}
                      yAxisMax={skinTemperatureAxis.max}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 300,
                      }}
                    >
                      <Alert severity='info'>No pattern data available</Alert>
                    </Box>
                  )
                ) : skinTemperaturePatternData.length > 0 ? (
                  <LineChart
                    data={skinTemperaturePatternData}
                    xAxisKey='time'
                    series={[
                      {
                        dataKey: 'value',
                        label: 'Skin Temperature',
                        color: theme.palette.warning.main,
                      },
                    ]}
                    height={300}
                    yAxisMin={skinTemperatureAxis.min}
                    yAxisMax={skinTemperatureAxis.max}
                    showMinMaxLines
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                    }}
                  >
                    <Alert severity='info'>No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
